<template>
  <div>
    <div
      v-if="!(newStores.length && changelogEntries.length)"
      class="pt-3"
    />
    <v-layout
      wrap
      justify-center
      align-center
    >
      <!-- Welcome -->
      <h3
        class="display-2 mx-4 my-3 d-flex"
        data-cy="dashboard-headers"
        style="align-items: center; flex-wrap: wrap"
      >
        <v-layout justify-center>
          {{ $t('greeting')
          }}<span v-if="!!currentUser && !!currentUser.first_name">,&nbsp;</span><span v-else>&nbsp;</span>
        </v-layout>
        <v-slide-y-transition>
          <div
            v-if="currentUser"
            class="clickable"
            @click="goToAccount()"
          >
            <span
              class="full-greeting"
              data-hj-suppress
            >
              <v-layout
                wrap
                align-center
                justify-center
              >
                <span
                  v-if="currentUser.first_name"
                >{{ currentUser.first_name }}&nbsp;</span>
                <img
                  src="/static/icons8-so-so-100.png"
                  width="64px"
                >
              </v-layout>
            </span>
          </div>
        </v-slide-y-transition>
      </h3>
      <div
        v-if="currentUser && (!loading || dashboardInfo)"
        class="mx-4 my-3"
      >
        <v-layout align-end>
          <!-- Number of Sites -->
          <dashboard-indicator-card
            v-if="dashboardInfo && dashboardInfo.num_stores"
            :number="dashboardInfo.num_stores"
            :number-unseen="numUnseen.sites"
            :subtitle="
              dashboardInfo.num_stores === 1 ? $t('site') : $t('sites')
            "
            icon="location_on"
            @click="$router.push('/sites')"
          />
          <!-- Number of TVSpots -->
          <dashboard-indicator-card
            v-if="dashboardInfo && dashboardInfo.num_available_tvspots"
            :number="dashboardInfo.num_available_tvspots"
            :number-unseen="numUnseen.tv_contents"
            :subtitle="$t('tvSpots')"
            icon="tv"
            @click="
              goToFilteredContent({radio: false, tv: true, tickers: true})
            "
          />
          <!-- Number of RadioSpots -->
          <dashboard-indicator-card
            v-if="dashboardInfo && dashboardInfo.num_available_radiospots"
            :number="dashboardInfo.num_available_radiospots"
            :number-unseen="numUnseen.radio_contents"
            :subtitle="$t('radioSpots')"
            icon="radio"
            @click="
              goToFilteredContent({radio: true, tv: false, tickers: false})
            "
          />
          <!-- Visitorcount 'lock' or current number of people -->
          <v-slide-x-transition>
            <dashboard-indicator-card
              v-if="dashboardVisitorcountInfo"
              :number="dashboardVisitorcountInfo.num_people_in_stores_right_now"
              :subtitle="
                dashboardVisitorcountInfo.is_demo_data
                  ? $t('visitorcount')
                  : $t('visitorcountDashboardWidgetLabel')
              "
              :visitorcount-demo-mode="dashboardVisitorcountInfo.is_demo_data"
              :icon="dashboardVisitorcountInfo.is_demo_data ? '' : 'timeline'"
              :title="$t('visitorcount')"
              @click="redirectToVisitorcountPage()"
            />
          </v-slide-x-transition>
        </v-layout>
      </div>
    </v-layout>
    <!-- 'Managed user has no access yet' warning -->
    <v-alert
      v-if="managedUserHasNoAccessYet"
      value="true"
      outline
      color="warning"
      icon="warning"
      class="mt-3 clickable"
      @click.stop="$router.push({name: 'userManagementManage'})"
    >
      {{ $t('userManagementNoAccessConfiguredYet') }}
    </v-alert>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading && nothingLoadedYet"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Generic Email Domain Warning -->
    <v-slide-y-transition>
      <generic-email-warning-with-button
        v-if="$userprofile && $userprofile.is_email_domain_generic"
      />
    </v-slide-y-transition>
    <div
      v-if="$vuetify.breakpoint.smAndUp"
      style="height: 1em"
    />
    <!-- Dashboard Advertisements Carousel -->
    <v-slide-x-reverse-transition>
      <v-layout
        v-if="dashboardAdvertisements.length > 0"
        justify-center
      >
        <dashboard-advertisements-carousel
          class="mb-5"
          :class="{
            'dashboard-adverts-align-with-tvspots-preview':
              $vuetify.breakpoint.mdAndUp,
          }"
        />
      </v-layout>
    </v-slide-x-reverse-transition>
    <!-- "What's new" aka Changelog -->
    <v-slide-y-reverse-transition>
      <div
        v-if="changelogEntries.length"
        class="mb-4"
      >
        <h4
          class="grey--text mb-2 text-xs-center text--darken-2 dashboard-header"
        >
          {{ $t('whatsNew') }}
        </h4>
        <div>
          <changelog-entry
            v-for="(changelogentry, changelogentryIndex) in changelogEntries"
            :key="changelogentry.id"
            :changelogentry="changelogentry"
            :show-divider="changelogentryIndex < changelogEntries.length - 1"
          />
        </div>
      </div>
    </v-slide-y-reverse-transition>
    <!-- Latest Stores -->
    <v-slide-y-reverse-transition>
      <div
        v-if="newStores.length"
        class="mb-4"
      >
        <h4
          class="grey--text mb-2 text-xs-center text--darken-2 dashboard-header"
        >
          {{ $t('newStores') }}
        </h4>
        <v-list
          v-for="(store, storeIndex) in newStores"
          :key="store.id"
          dense
        >
          <store-tile
            :store="store"
            :show-new-badge="false"
            :show-divider="storeIndex < newStores.length - 1"
            show-date
            @click="goToSitesWithSearchText(store.city + ' ' + store.name)"
          >
            <div slot="actions" />
          </store-tile>
        </v-list>
      </div>
    </v-slide-y-reverse-transition>
    <!-- Latest Content -->
    <v-slide-y-reverse-transition>
      <div
        v-if="!loading && loadingLatestContent && !latestSpots.length"
        class="loading-indicator"
      >
        <v-progress-circular
          :size="50"
          indeterminate
          class="mt-5 msr--text"
        />
      </div>
      <div v-else-if="latestSpots.length">
        <h4
          class="grey--text mb-4 text-xs-center text--darken-2 dashboard-header"
        >
          {{ $t('latestContent') }}
        </h4>
        <div>
          <template v-for="(spot, spotIndex) in latestSpots">
            <tv-spot-bundle
              v-if="isTVSpotBundle(spot)"
              :key="spot.ident"
              :bundle="spot"
              :show-divider="spotIndex < latestSpots.length - 1"
              :show-new-badge="false"
              no-content-selection-placeholder
              show-date
            />
            <spot
              v-if="isSpot(spot) && !isUploadImage(spot)"
              :key="spot.ident"
              :ctype="spot.ctype"
              :show-divider="spotIndex < latestSpots.length - 1"
              :show-new-badge="false"
              :spot="spot"
              no-content-selection-placeholder
              show-date
            />
          </template>
        </div>
      </div>
    </v-slide-y-reverse-transition>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';
import {mapState} from 'vuex';

import {redirectToExternalUrl} from '@/utils';
import identifiers from '@/mixins/identifiers.js';

import ChangelogEntry from '@/components/ChangelogEntry';
import DashboardAdvertisementsCarousel from '@/components/DashboardAdvertisementsCarousel';
import DashboardIndicatorCard from '@/components/DashboardIndicatorCard';
import GenericEmailWarningWithButton from '@/components/GenericEmailWarningWithButton';
import Spot from '@/components/Spot';
import StoreTile from '@/components/StoreTile';
import TvSpotBundle from '@/components/TvSpotBundle';

export default {
  components: {
    ChangelogEntry,
    DashboardAdvertisementsCarousel,
    DashboardIndicatorCard,
    GenericEmailWarningWithButton,
    Spot,
    StoreTile,
    TvSpotBundle,
  },
  mixins: [identifiers],
  data() {
    return {
      loading: false,
      loadingLatestContent: false,
    };
  },
  computed: {
    ...mapState({
      changelogEntries: (state) => state.common.changelogEntries,
      currentUser: (state) => state.common.currentUser,
      dashboardInfo: (state) => state.common.dashboardInfo,
      dashboardVisitorcountInfo: (state) =>
        state.common.dashboardVisitorcountInfo,
      dashboardAdvertisements: (state) => state.common.dashboardAdvertisements,
      latestSpots: (state) => state.common.latestSpots,
      numUnseen: (state) => state.common.numUnseen,
    }),
    managedUserHasNoAccessYet() {
      if (this.$store.getters.isManagedCoadmin) {
        // Access is probably implicit, so no need to show the warning.
        return false;
      }
      const isManagedUser = this.$store.getters.userIsBeingManaged;
      const hasNoContents =
        this.dashboardInfo &&
        this.dashboardInfo.num_available_tvspots === 0 &&
        this.dashboardInfo.num_available_radiospots === 0;
      const hasNoStores =
        this.dashboardInfo && this.dashboardInfo.num_stores === 0;
      return isManagedUser && hasNoContents && hasNoStores;
    },
    newStores() {
      if (!this.dashboardInfo) {
        return [];
      }
      return this.dashboardInfo.new_stores || [];
    },
    nothingLoadedYet() {
      return !this.latestSpots.length && !this.dashboardInfo;
    },
  },
  mounted() {
    this.fetchDashboardData();
  },
  methods: {
    goToSitesWithSearchText(searchText) {
      this.$router.push({
        name: 'sites',
        query: {search: searchText},
      });
    },
    goToSpot(ctype, spotId) {
      const path = '/spot/' + ctype + '/' + spotId;
      this.$router.push(path);
    },
    goToAccount() {
      this.$router.push('/account');
    },
    goToFilteredContent(contentTypes) {
      this.$store.commit('SET_CONTENTS_QUERY_RADIO', contentTypes.radio);
      this.$store.commit('SET_CONTENTS_QUERY_TV', contentTypes.tv);
      this.$store.commit('SET_CONTENTS_QUERY_TICKERS', contentTypes.tickers);

      this.$router.push({path: '/content'});
    },
    fetchDashboardData() {
      this.loading = true;

      const infoPromise = this.$api.retrieveDashboardInfo();
      infoPromise.then((response) => {
        this.$store.commit('SET_DASHBOARD_INFO', response.data);
      });

      const visitorcountPromise =
        this.$api.retrieveVisitorcountDashboardWidgetInfo();
      visitorcountPromise.then((response) => {
        this.$store.commit('SET_DASHBOARD_VISITORCOUNT_INFO', response.data);
      });

      const changelogPromise = this.$api.listChangelogEntries();
      changelogPromise.then((response) => {
        this.$store.commit('SET_CHANGELOG_ENTRIES', response.data);
      });

      // Regarding 'unseen content' information we rely on the
      // AppDrawer to fetch that.
      this.loadingLatestContent = true;
      const latestContentPromise = this.$api.listLatestContents();
      latestContentPromise
        .then((spots) => {
          this.$store.commit(
            'SET_LATEST_SPOTS',
            orderBy(spots, 'date', 'desc')
          );
        })
        .finally(() => {
          this.loadingLatestContent = false;
        });

      // TODO: Lots of requests here, maybe we can merge some of them
      //   into less endpoints/calls?
      Promise.all([
        infoPromise,
        visitorcountPromise,
        changelogPromise,
        // latestContentPromise,
      ]).finally(() => {
        this.loading = false;

        const advertisementsPromise = this.$api.listDashboardAdvertisements();
        advertisementsPromise.then((response) => {
          this.$store.commit('SET_DASHBOARD_ADVERTISEMENTS', response.data);
        });
      });
    },
    redirectToVisitorcountPage() {
      redirectToExternalUrl(this, '/client/visitorcount');
    },
  },
};
</script>

<style scoped>
@media (max-width: 599px) and (orientation: portrait) {
  .spot-tile {
    flex-flow: column nowrap;
    align-items: flex-start;
  }
}

.date {
  margin-right: 1em;
}

.dashboard-section-title {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dashboard-header {
  font-size: 20px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  text-align: center;
}

.custom-carousel-look >>> .v-carousel__prev,
.custom-carousel-look >>> .v-carousel__next {
  background: rgba(0, 0, 0, 0.16);
}

.custom-carousel-look >>> .v-carousel__controls {
  background: transparent;
}
.custom-carousel-look >>> .v-carousel__controls .v-item-group {
  background: rgba(0, 0, 0, 0.16);
  padding: 12px 8px;
}
.custom-carousel-look >>> .v-carousel__controls__item .v-icon {
  opacity: 0.75;
}
.custom-carousel-look >>> .v-carousel__controls__item.v-btn--active {
  background: rgba(255, 255, 255, 0.25);
}

.dashboard-adverts-align-with-tvspots-preview {
  /* 40px is equal to the mini-video-preview for 16:9 TVSpots. */
  margin-left: 40px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Dashboard.vue